import { useEffect, useState } from "react";

type BookingPayPalReturnPageProps = {
  apiBaseUrl: string;
  orderId: string;
};

type CaptureResponse = {
  orderId: string;
  orderStatus: string;
  captureId: string;
  captureStatus: string;
  paymentState: "COMPLETED" | "PENDING";
  message: string;
  travelName?: string;
  travelDestination?: string | null;
};

const WHATSAPP_SUPPORT_URL = `https://wa.me/8617318902873?text=${encodeURIComponent(
  "Hi Fulcrum Travel, I already paid but got an error page. Please help me confirm my payment."
)}`;
const WHATSAPP_CHAT_URL = `https://wa.me/8617318902873?text=${encodeURIComponent(
  "Hi Fulcrum Travel, I have completed my booking payment and would like to discuss the itinerary."
)}`;

const captureRequestCache = new Map<string, Promise<CaptureResponse>>();

function requestCaptureOnce(apiBaseUrl: string, orderId: string) {
  const cacheKey = `${apiBaseUrl}::${orderId}`;
  const cached = captureRequestCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const request = fetch(`${apiBaseUrl}/api/public/booking/paypal/capture-order`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ orderId })
  }).then(async (response) => {
    const payload = (await response
      .json()
      .catch(() => null)) as
      | {
          success?: boolean;
          data?: CaptureResponse;
          error?: string;
        }
      | null;

    if (!response.ok || !payload?.success || !payload.data) {
      throw new Error(payload?.error || "Failed to complete PayPal payment.");
    }

    return payload.data;
  });

  captureRequestCache.set(cacheKey, request);
  return request;
}

export default function BookingPayPalReturnPage({
  apiBaseUrl,
  orderId
}: BookingPayPalReturnPageProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CaptureResponse | null>(null);

  useEffect(() => {
    let disposed = false;
    setLoading(true);
    setError(null);
    setResult(null);

    void requestCaptureOnce(apiBaseUrl, orderId)
      .then((data) => {
        if (!disposed) {
          setResult(data);
        }
      })
      .catch((reason) => {
        if (!disposed) {
          setError(
            reason instanceof Error ? reason.message : "Failed to complete PayPal payment."
          );
        }
      })
      .finally(() => {
        if (!disposed) {
          setLoading(false);
        }
      });

    return () => {
      disposed = true;
    };
  }, [apiBaseUrl, orderId]);

  return (
    <section className="mx-auto w-full max-w-[920px] px-4 py-10 md:px-8 lg:px-10">
      <div className="rounded-3xl bg-white p-8 shadow-[0_18px_40px_rgba(15,23,36,0.12)]">
        <p className="text-xs font-semibold uppercase tracking-[0.32em] text-[#f7b500]">
          PayPal Return
        </p>

        {loading ? (
          <p className="mt-4 text-sm text-[#6b7280]">Finalizing your payment...</p>
        ) : error ? (
          <div className="mt-4 rounded-2xl border border-[#fecaca] bg-[#fff1f2] p-4 text-sm text-[#b91c1c]">
            <p>{error}</p>
            <a
              href={WHATSAPP_SUPPORT_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex rounded-xl border border-[#16a34a] bg-white px-4 py-2 text-sm font-semibold text-[#15803d] transition hover:bg-[#dcfce7]"
            >
              Contact us on WhatsApp
            </a>
          </div>
        ) : result ? (
          <div
            className={`mt-4 rounded-2xl border p-5 ${
              result.paymentState === "COMPLETED"
                ? "border-[#86efac] bg-[#f0fdf4]"
                : "border-[#fde68a] bg-[#fffbeb]"
            }`}
          >
            <h1 className="text-xl font-semibold text-[#111827]">
              {result.paymentState === "COMPLETED"
                ? "Payment completed"
                : "Payment pending confirmation"}
            </h1>
            <p className="mt-2 text-sm text-[#111827]">{result.message}</p>
            {result.paymentState === "COMPLETED" && (
              <div className="mt-3 rounded-xl border border-[#86efac] bg-[#f0fdf4] p-3 text-sm text-[#166534]">
                Our travel consultant will contact you soon to discuss your itinerary. You can
                also reach us directly on WhatsApp.
                <a
                  href={WHATSAPP_CHAT_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex rounded-lg border border-[#16a34a] bg-white px-3 py-1.5 text-xs font-semibold text-[#15803d] transition hover:bg-[#dcfce7]"
                >
                  Contact on WhatsApp
                </a>
              </div>
            )}
            {result.travelName && (
              <p className="mt-2 text-xs text-[#4b5563]">
                Trip: {result.travelName}
                {result.travelDestination ? ` · ${result.travelDestination}` : ""}
              </p>
            )}
            <p className="mt-1 text-xs text-[#4b5563]">
              Order: {result.orderId} · Capture: {result.captureId}
            </p>
            <button
              type="button"
              onClick={() => {
                window.location.href = "/";
              }}
              className="mt-4 rounded-xl border border-[#d1d5db] bg-white px-5 py-3 text-sm font-semibold text-[#1f2937] transition hover:border-[#0d5be1] hover:text-[#0d5be1]"
            >
              Back to home
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
